//@flow

import React, {useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {Box, Typography, CircularProgress} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';
import CancelIcon from '@material-ui/icons/Cancel';
import CustomTextField from '../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import ConfirmModal from '../../../elements/Modals/ConfirmModal';
import SortableList from 'react-easy-sort';
import ProductOptionItem from './ProductOptionItem';
import {useProductOptionGroupActions} from '../utils';
import {
  useProductOptionFormActions,
  useStoreSelector,
  ProductOptionFormProvider,
} from './ProductOptionFormContext';
import type {StoreProductOptionGroup} from '../types/productOptions';
import {colors} from '../../../assets/styles/colors';
import {classNames} from '../../../utils/react/className';

const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: '.75rem',
    background: 'white',
    boxShadow: '0 .5rem 1rem rgba(184, 181, 181, 0.16)',
    transition: 'all 0.2s ease-in-out',
  },
  cardContent: {
    padding: '.75rem !important',
  },
  editingCard: {
    boxShadow: `0 .5rem 1.5rem rgba(${colors.primary}, 0.25)`,
    border: `2px solid ${colors.primary}`,
  },
  groupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    [theme.breakpoints.up('sm')]: {
      alignItems: 'center',
    },
  },
  groupInfo: {
    flex: 1,
  },
  groupName: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: colors.grey900,
    marginBottom: theme.spacing(0.5),
  },
  groupDescription: {
    fontSize: '0.875rem',
    color: colors.grey700,
    lineHeight: 1.4,
    display: '-webkit-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
  },
  switchSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: theme.spacing(2),
  },
  switchLabel: {
    fontSize: '0.875rem',
    color: colors.grey700,
  },
  actionsSection: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '.5rem',
    borderTop: `1px solid ${colors.grey200}`,
  },
  actionButton: {
    padding: '.5rem',
    minWidth: 'auto',
    color: colors.grey700,
    '&:hover': {
      backgroundColor: colors.grey100,
    },
  },
  editButton: {
    color: colors.primary,
  },
  deleteButton: {
    color: colors.error,
  },
  saveButton: {
    color: colors.success,
  },
  cancelButton: {
    color: colors.grey600,
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    backgroundColor: colors.grey50,
    borderRadius: '0.5rem',
  },
  formTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey700,
  },
  optionsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  addOptionButton: {
    backgroundColor: colors.grey200,
    color: colors.grey700,
    borderStyle: 'dashed',
    borderWidth: '2px',
    borderColor: colors.grey400,
    padding: '0.75rem',
    '&:hover': {
      backgroundColor: colors.grey300,
      borderColor: colors.grey500,
    },
  },
  loadingOverlay: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '1rem',
      zIndex: 1,
    },
  },
  errorAlert: {
    marginTop: '1rem',
  },
  optionName: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: colors.grey700,
    padding: '0.25rem 0.5rem',
    borderRadius: '0.5rem',
    background: colors.grey200,
  },
  formActionsSection: {
    position: 'sticky',
    bottom: 0,
    paddingBlock: '.75rem',
    backgroundColor: '#ffffffc7',
    zIndex: 1,
    backdropFilter: 'blur(.75rem)',
  },
}));

type Props = {
  group?: StoreProductOptionGroup,
  isNew?: boolean,
  prefilledData?: any,
  onSave?: (group: StoreProductOptionGroup) => void,
  onCancel?: () => void,
  onDelete?: () => void,
};

const mapGroupToFormData = groupData => {
  const formData = {
    name: groupData.name || '',
    description: groupData.description || '',
    enabled: groupData.enabled,
  };

  let options = [{name: '', enabled: true, price: 0}];
  if (groupData.options && groupData.options.length > 0) {
    const sorted = [...groupData.options].sort((a, b) => a.number - b.number);
    options = sorted.map(option => ({
      name: option.name || '',
      color: option.color,
      enabled: !!option.enabled,
      price: option.price || 0,
      storeProductOptionId: option.id,
      imageId: option.imageId,
      id: option.id,
    }));
  }

  return {formData, options};
};

function ProductOptionGroupInlineFormContent({
  group,
  isNew = false,
  prefilledData,
  onSave,
  onCancel,
  onDelete,
}: Props) {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(isNew);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

  const {
    createGroup,
    updateGroup,
    deleteGroup,
    loading,
  } = useProductOptionGroupActions();

  const formData = useStoreSelector(s => s.$formData);
  const groupOptions = useStoreSelector(s => s.$options);
  const errors = useStoreSelector(s => s.$errors);
  const isValid = useStoreSelector(s => s.$isValid);

  const {
    updateGroupName,
    updateGroupDescription,
    addOption,
    moveOption,
    validateForm,
    resetForm,
  } = useProductOptionFormActions();

  const onSortEnd = (oldIndex: number, newIndex: number) => {
    moveOption(oldIndex, newIndex);
  };

  const handleAddOption = () => {
    addOption();
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    if (isNew) {
      if (onCancel) {
        onCancel();
      }
    } else {
      setIsEditing(false);
      setError(null);
      if (group) {
        const {
          formData: originalFormData,
          options: originalOptions,
        } = mapGroupToFormData(group);
        resetForm(originalFormData, originalOptions);
      }
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!group?.id) return;

    setShowDeleteConfirmModal(false);
    setError(null);
    try {
      const success = await deleteGroup(group.id);
      if (success && onDelete) {
        onDelete();
      }
    } catch (err) {
      setError('Error al eliminar el grupo de opciones');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmModal(false);
  };

  const handleSubmit = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    try {
      if (isNew) {
        const createRequestData = {
          name: formData.name,
          description: formData.description,
          enabled: formData.enabled,
          options: groupOptions.map((option, index) => ({
            name: option.name,
            color: option.color || undefined,
            number: index,
            enabled: option.enabled,
            price: option.price ? option.price.toString() : '0',
            imageId: option.imageId,
            imageFile: option.imageFile,
            storeProductOptionId: option.storeProductOptionId || undefined,
          })),
        };
        const newGroup = await createGroup(createRequestData);
        if (newGroup && onSave) {
          onSave(newGroup);
        }
      } else if (group?.id) {
        const updateRequestData = {
          name: formData.name,
          description: formData.description,
          enabled: formData.enabled,
          options: groupOptions.map((option, index) => ({
            name: option.name,
            color: option.color || undefined,
            number: index,
            enabled: option.enabled,
            price: option.price ? option.price.toString() : '0',
            imageId: option.imageId,
            imageFile: option.imageFile,
            storeProductOptionId: option.storeProductOptionId || undefined,
          })),
        };

        const updatedGroup = await updateGroup(group.id, updateRequestData);
        if (updatedGroup && onSave) {
          onSave(updatedGroup);
          setIsEditing(false);
        }
      }
    } catch (err) {
      console.log({err});
      setError(
        isNew
          ? 'Error al crear el grupo de opciones'
          : 'Error al actualizar el grupo de opciones'
      );
    }
  };

  const cardClassName = classNames(classes.root, {
    [classes.editingCard]: isEditing,
    [classes.loadingOverlay]: loading,
  });

  if (isEditing) {
    return (
      <div className={cardClassName}>
        <div className={classes.cardContent}>
          <div>
            <Box className={classes.formSection}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                justifyContent="space-between"
              >
                <Typography className={classes.formTitle}>
                  {isNew
                    ? 'Nuevo grupo de opciones'
                    : 'Editando grupo de opciones'}
                </Typography>
              </Box>

              <CustomTextField
                onChange={e => updateGroupName(e.target.value)}
                value={formData.name}
                label="Nombre del grupo"
                fullWidth
                error={!!errors.name}
                helperText={errors.name}
              />

              <CustomTextField
                onChange={e => updateGroupDescription(e.target.value)}
                value={formData.description || ''}
                label="Instrucciones / Descripción"
                fullWidth
                multiline
                minRows={2}
                maxRows={5}
              />

              <Typography variant="subtitle2" color="primary">
                Opciones del grupo
              </Typography>

              <SortableList
                onSortEnd={onSortEnd}
                className={classes.optionsSection}
                draggedItemClassName="dragged"
              >
                {groupOptions.map((option, index) => (
                  <ProductOptionItem
                    key={option.id}
                    canDelete={groupOptions.length > 1}
                    option={option}
                  />
                ))}
              </SortableList>

              <CustomButton
                className={classes.addOptionButton}
                onClick={handleAddOption}
                startIcon={<AddIcon />}
                fullWidth
              >
                Agregar nueva opción
              </CustomButton>

              {error && (
                <Alert severity="error" className={classes.errorAlert}>
                  {error}
                </Alert>
              )}

              <Box
                display="flex"
                className={classes.formActionsSection}
                justifyContent="flex-end"
              >
                <CustomButton
                  onClick={handleCancel}
                  disabled={loading}
                  className={classes.cancelButton}
                  startIcon={<CancelIcon />}
                >
                  Cancelar
                </CustomButton>
                <CustomButton
                  onClick={handleSubmit}
                  disabled={!isValid || loading}
                  variant="contained"
                  color="primary"
                  startIcon={
                    loading ? <CircularProgress size={16} /> : <SaveIcon />
                  }
                >
                  {loading ? 'Guardando...' : 'Guardar'}
                </CustomButton>
              </Box>
            </Box>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cardClassName}>
      <div className={classes.cardContent}>
        <Box className={classes.groupHeader}>
          <Box className={classes.groupInfo}>
            <Typography className={classes.groupName}>
              {group?.name || 'Grupo sin nombre'}
            </Typography>
            {group?.description && (
              <Typography className={classes.groupDescription}>
                {group.description}
              </Typography>
            )}

            <Box
              display="flex"
              alignItems="center"
              style={{
                flexWrap: 'wrap',
                gap: '0.5rem',
              }}
              my={1}
            >
              {group?.options?.map((option, index) => (
                <span key={option.id} className={classes.optionName}>
                  {option.name}
                </span>
              ))}
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" className={classes.errorAlert}>
            {error}
          </Alert>
        )}

        <Box className={classes.actionsSection}>
          <CustomButton
            className={`${classes.actionButton} ${classes.editButton}`}
            onClick={handleEdit}
            size="small"
            disabled={loading}
            startIcon={<EditIcon fontSize="small" />}
          >
            Editar
          </CustomButton>
          {!isNew && (
            <CustomButton
              className={`${classes.actionButton} ${classes.deleteButton}`}
              onClick={handleDeleteClick}
              size="small"
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={16} />
                ) : (
                  <DeleteIcon fontSize="small" />
                )
              }
            >
              Eliminar
            </CustomButton>
          )}
        </Box>

        {loading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor="rgba(255, 255, 255, 0.8)"
            borderRadius="1rem"
            zIndex={1}
          >
            <CircularProgress />
          </Box>
        )}
      </div>

      <ConfirmModal
        open={showDeleteConfirmModal}
        onClose={handleDeleteCancel}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="¿Eliminar grupo de opciones?"
        message="Al eliminar este grupo de opciones, también se eliminará de todos los productos en los que esté configurado. Esta acción no se puede deshacer."
        confirmButtonLabel="Eliminar"
        cancelButtonLabel="Cancelar"
      />
    </div>
  );
}

export default function ProductOptionGroupInlineForm(props: Props) {
  const getInitialData = () => {
    const prefilledData = props.prefilledData;
    const group = props.group;
    const id = crypto.randomUUID();

    if (prefilledData) {
      return {
        formData: {
          name: prefilledData.name || '',
          description: prefilledData.description || '',
          enabled: prefilledData.enabled !== false,
        },
        options: prefilledData.options || [
          {name: '', enabled: true, price: 0, id},
        ],
      };
    }

    if (group) {
      return mapGroupToFormData(group);
    }

    return {
      formData: {
        name: '',
        description: '',
        enabled: true,
      },
      options: [{name: '', enabled: true, price: 0, id}],
    };
  };

  const {formData: initialFormData, options: initialOptions} = getInitialData();

  const initialState = {
    formData: initialFormData,
    options: initialOptions,
    errors: {},
    isValid: false,
  };

  return (
    <ProductOptionFormProvider initial={initialState}>
      <ProductOptionGroupInlineFormContent {...props} />
    </ProductOptionFormProvider>
  );
}
